import { initAnalytics, revokeAnalytics } from '../../lib/analytics';
import {
  ALL_COOKIE_PREFERENCES,
  DEFAULT_COOKIE_PREFERENCES,
  normalizePreferences,
} from './cookieCategories';
import {
  clearConsentRecord,
  readConsentRecord,
  writeConsentRecord,
} from './CookieConsentStore';
import { COOKIE_CONSENT_VERSION, type CookieConsentPreferences, type CookieConsentRecord } from './types';

type ConsentListener = (record: CookieConsentRecord | null) => void;

/**
 * Çerez onayı servis katmanı — GDPR açık rıza ve tercih yönetimi.
 */
export class CookieConsentService {
  private listeners = new Set<ConsentListener>();

  getConsent(): CookieConsentRecord | null {
    return readConsentRecord();
  }

  hasConsent(): boolean {
    return this.getConsent() !== null;
  }

  isCategoryEnabled(category: keyof CookieConsentPreferences): boolean {
    const record = this.getConsent();
    if (!record) return category === 'essential';
    return record.preferences[category];
  }

  subscribe(listener: ConsentListener): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  acceptAll(): CookieConsentRecord {
    return this.savePreferences(ALL_COOKIE_PREFERENCES);
  }

  acceptEssentialOnly(): CookieConsentRecord {
    return this.savePreferences(DEFAULT_COOKIE_PREFERENCES);
  }

  savePreferences(partial: Partial<CookieConsentPreferences>): CookieConsentRecord {
    const preferences = normalizePreferences(partial);
    const record: CookieConsentRecord = {
      version: COOKIE_CONSENT_VERSION,
      updatedAt: new Date().toISOString(),
      preferences,
    };
    writeConsentRecord(record);
    this.applyConsent(record);
    this.notify(record);
    return record;
  }

  clearConsent(): void {
    clearConsentRecord();
    revokeAnalytics();
    this.notify(null);
  }

  applyConsent(record?: CookieConsentRecord | null): void {
    const consent = record ?? this.getConsent();
    if (!consent) return;

    if (consent.preferences.analytics) {
      initAnalytics();
    } else {
      revokeAnalytics();
    }
  }

  private notify(record: CookieConsentRecord | null): void {
    for (const listener of this.listeners) {
      listener(record);
    }
  }
}

let singleton: CookieConsentService | null = null;

export function getCookieConsentService(): CookieConsentService {
  if (!singleton) {
    singleton = new CookieConsentService();
  }
  return singleton;
}
